import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { products as defaultProducts, categories as defaultCategories, Product, Category } from '../../data/products';
import { 
  AdminState, 
  Order, 
  OrderStatus, 
  StoreSettings,
  Notification,
  Customer,
  Coupon
} from '../types/admin';

const API_ROOT = () => (import.meta.env.VITE_API_URL || 'http://localhost:3001');
const ORDERS_API_BASE = () => API_ROOT() + '/api/orders';
const PRODUCTS_API_BASE = () => API_ROOT() + '/api/products';
const CATEGORIES_API_BASE = () => API_ROOT() + '/api/categories';
const COUPONS_API_BASE = () => API_ROOT() + '/api/coupons';
const SETTINGS_API_BASE = () => API_ROOT() + '/api/settings';
const ADMIN_API_BASE = () => API_ROOT() + '/api/admin';

// Admin'e özel yazma isteklerine kimlik doğrulama başlığını ekleyen yardımcı.
const authHeaders = (token: string | null): HeadersInit =>
  token ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } : { 'Content-Type': 'application/json' };

// Ürün/sipariş kayıt isteklerini gönderir ve başarısızlığı (401, doğrulama
// hatası, ağ hatası) admin'e GÖRÜNÜR bir bildirim olarak düşürür. Önceden
// hatalar sadece console.error ile geçiliyordu; bu yüzden bir oturum
// süresi dolduğunda veya token geçersiz olduğunda değişiklikler admin
// panelinde "kaydedildi" gibi görünüp aslında veritabanına hiç ulaşmıyordu.
async function syncWrite(
  addNotification: (n: any) => void,
  url: string,
  options: RequestInit,
  failMessage: string
) {
  try {
    const response = await fetch(url, options);
    let data: any = null;
    try { data = await response.json(); } catch { /* boş/JSON olmayan yanıt */ }

    if (!response.ok || (data && data.success === false)) {
      const reason = response.status === 401
        ? 'Oturumunuz sona ermiş görünüyor. Lütfen çıkış yapıp tekrar giriş yapın.'
        : (data?.error || `Sunucu hatası (${response.status})`);
      addNotification({
        type: 'error',
        title: 'Kaydedilemedi!',
        message: `${failMessage} ${reason}`,
        icon: 'status',
      });
      return false;
    }
    return true;
  } catch (err) {
    addNotification({
      type: 'error',
      title: 'Bağlantı Hatası',
      message: `${failMessage} Sunucuya ulaşılamadı, internet bağlantınızı kontrol edin.`,
      icon: 'status',
    });
    return false;
  }
}


const defaultSettings: StoreSettings = {
  storeName: 'T-RaX Car Care',
  storeEmail: 'info@sbtkimya.com.tr',
  storePhone: '0312 815 11 65',
  storeAddress: 'Yeni Birlik Sanayi Sitesi, Saray, 675. Cadde No:24/12, 06980 Kahramankazan/Ankara',
  currency: 'TRY',
  shippingCost: 129,
  freeShippingMin: 1000,
  taxRate: 20,
  maintenanceMode: false,
};

const generateOrderId = () => `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const generateCustomerId = () => `CUS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const generateOrderNumber = () => {
  const now = new Date();
  const prefix = 'TRX';
  const date = now.getFullYear().toString().slice(2) +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefix}-${date}-${random}`;
};

const sampleOrders: Order[] = [
  {
    id: generateOrderId(),
    orderNumber: 'TRX-260720-0001',
    customerName: 'Ahmet Yılmaz',
    customerEmail: 'ahmet@email.com',
    customerPhone: '0532 111 2233',
    shippingAddress: 'Atatürk Mah. Cumhur Cad. No:15',
    city: 'Ankara',
    district: 'Çankaya',
    postalCode: '06680',
    items: [
      { product: defaultProducts[0], quantity: 2 },
      { product: defaultProducts[1], quantity: 1 },
    ],
    totalAmount: defaultProducts[0].salePrice * 2 + defaultProducts[1].salePrice,
    shippingCost: 0,
    status: 'delivered',
    paymentMethod: 'credit_card',
    paymentStatus: 'paid',
    notes: '',
    createdAt: '2026-07-20T10:30:00Z',
    updatedAt: '2026-07-22T14:00:00Z',
  },
  {
    id: generateOrderId(),
    orderNumber: 'TRX-260721-0002',
    customerName: 'Mehmet Kaya',
    customerEmail: 'mehmet@email.com',
    customerPhone: '0535 444 5566',
    shippingAddress: 'İstiklal Cad. No:42/3',
    city: 'İstanbul',
    district: 'Beyoğlu',
    postalCode: '34430',
    items: [
      { product: defaultProducts[2], quantity: 3 },
    ],
    totalAmount: defaultProducts[2].salePrice * 3,
    shippingCost: 129,
    status: 'shipped',
    paymentMethod: 'bank_transfer',
    paymentStatus: 'paid',
    notes: 'Acil teslimat istendi',
    createdAt: '2026-07-21T15:45:00Z',
    updatedAt: '2026-07-23T09:00:00Z',
  },
  {
    id: generateOrderId(),
    orderNumber: 'TRX-260722-0003',
    customerName: 'Ayşe Demir',
    customerEmail: 'ayse@email.com',
    customerPhone: '0542 777 8899',
    shippingAddress: 'Atatürk Bulvarı No:128',
    city: 'Ankara',
    district: 'Yenimahalle',
    postalCode: '06170',
    items: [
      { product: defaultProducts[3], quantity: 1 },
      { product: defaultProducts[4], quantity: 2 },
    ],
    totalAmount: defaultProducts[3].salePrice + defaultProducts[4].salePrice * 2,
    shippingCost: 0,
    status: 'pending',
    paymentMethod: 'credit_card',
    paymentStatus: 'pending',
    notes: '',
    createdAt: '2026-07-22T08:15:00Z',
    updatedAt: '2026-07-22T08:15:00Z',
  },
  {
    id: generateOrderId(),
    orderNumber: 'TRX-260723-0004',
    customerName: 'Ali Çelik',
    customerEmail: 'ali@email.com',
    customerPhone: '0538 222 3344',
    shippingAddress: 'Kızılay Cad. No:5',
    city: 'Ankara',
    district: 'Çankaya',
    postalCode: '06420',
    items: [
      { product: defaultProducts[5], quantity: 1 },
    ],
    totalAmount: defaultProducts[5].salePrice,
    shippingCost: 129,
    status: 'processing',
    paymentMethod: 'credit_card',
    paymentStatus: 'paid',
    notes: 'Müşteri notu: Kapıya bırakılabilir',
    createdAt: '2026-07-23T12:00:00Z',
    updatedAt: '2026-07-23T12:30:00Z',
  },
  {
    id: generateOrderId(),
    orderNumber: 'TRX-260724-0005',
    customerName: 'Fatma Özkan',
    customerEmail: 'fatma@email.com',
    customerPhone: '0544 999 0011',
    shippingAddress: 'Bağdat Cad. No:200',
    city: 'İstanbul',
    district: 'Kadıköy',
    postalCode: '34728',
    items: [
      { product: defaultProducts[6], quantity: 2 },
      { product: defaultProducts[7], quantity: 1 },
    ],
    totalAmount: defaultProducts[6].salePrice * 2 + defaultProducts[7].salePrice,
    shippingCost: 0,
    status: 'in_transit',
    paymentMethod: 'credit_card',
    paymentStatus: 'paid',
    notes: '',
    createdAt: '2026-07-24T16:20:00Z',
    updatedAt: '2026-07-25T10:00:00Z',
  },
];

const sampleCustomers: Customer[] = [
  {
    id: generateCustomerId(),
    name: 'Ahmet Yılmaz',
    email: 'ahmet@email.com',
    phone: '0532 111 2233',
    address: 'Atatürk Mah. Cumhur Cad. No:15',
    city: 'Ankara',
    district: 'Çankaya',
    postalCode: '06680',
    notes: 'Düzenli müşteri',
    totalOrders: 3,
    totalSpent: 1250,
    lastOrderDate: '2026-07-20T10:30:00Z',
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-07-20T10:30:00Z',
  },
  {
    id: generateCustomerId(),
    name: 'Mehmet Kaya',
    email: 'mehmet@email.com',
    phone: '0535 444 5566',
    address: 'İstiklal Cad. No:42/3',
    city: 'İstanbul',
    district: 'Beyoğlu',
    postalCode: '34430',
    notes: '',
    totalOrders: 2,
    totalSpent: 890,
    lastOrderDate: '2026-07-21T15:45:00Z',
    createdAt: '2026-03-10T08:00:00Z',
    updatedAt: '2026-07-21T15:45:00Z',
  },
  {
    id: generateCustomerId(),
    name: 'Ayşe Demir',
    email: 'ayse@email.com',
    phone: '0542 777 8899',
    address: 'Atatürk Bulvarı No:128',
    city: 'Ankara',
    district: 'Yenimahalle',
    postalCode: '06170',
    notes: 'VIP müşteri',
    totalOrders: 5,
    totalSpent: 3200,
    lastOrderDate: '2026-07-22T08:15:00Z',
    createdAt: '2025-11-20T12:00:00Z',
    updatedAt: '2026-07-22T08:15:00Z',
  },
  {
    id: generateCustomerId(),
    name: 'Ali Çelik',
    email: 'ali@email.com',
    phone: '0538 222 3344',
    address: 'Kızılay Cad. No:5',
    city: 'Ankara',
    district: 'Çankaya',
    postalCode: '06420',
    notes: '',
    totalOrders: 1,
    totalSpent: 450,
    lastOrderDate: '2026-07-23T12:00:00Z',
    createdAt: '2026-07-23T12:00:00Z',
    updatedAt: '2026-07-23T12:00:00Z',
  },
  {
    id: generateCustomerId(),
    name: 'Fatma Özkan',
    email: 'fatma@email.com',
    phone: '0544 999 0011',
    address: 'Bağdat Cad. No:200',
    city: 'İstanbul',
    district: 'Kadıköy',
    postalCode: '34728',
    notes: 'Toptan alıcı',
    totalOrders: 8,
    totalSpent: 15600,
    lastOrderDate: '2026-07-24T16:20:00Z',
    createdAt: '2025-08-05T09:00:00Z',
    updatedAt: '2026-07-24T16:20:00Z',
  },
];

const generateCouponId = () => `CUP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const sampleCoupons: Coupon[] = [
  {
    id: generateCouponId(),
    code: 'TRX10',
    description: 'Tüm ürünlerde %10 indirim',
    type: 'percentage',
    value: 10,
    minOrderAmount: 0,
    maxDiscountAmount: null,
    usageLimit: 100,
    usageCount: 12,
    startDate: '2026-07-01',
    endDate: '2026-12-31',
    isActive: true,
    createdAt: '2026-07-01T00:00:00Z',
    updatedAt: '2026-07-01T00:00:00Z',
  },
  {
    id: generateCouponId(),
    code: 'YAZ50',
    description: '50₺ indirim (200₺ ve üzeri)',
    type: 'fixed',
    value: 50,
    minOrderAmount: 200,
    maxDiscountAmount: null,
    usageLimit: 50,
    usageCount: 8,
    startDate: '2026-06-01',
    endDate: '2026-09-30',
    isActive: true,
    createdAt: '2026-06-01T00:00:00Z',
    updatedAt: '2026-06-01T00:00:00Z',
  },
  {
    id: generateCouponId(),
    code: 'HOŞGELDİN',
    description: 'Yeni üyelere %15 indirim',
    type: 'percentage',
    value: 15,
    minOrderAmount: 100,
    maxDiscountAmount: 200,
    usageLimit: null,
    usageCount: 34,
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
];

const getLatestCoupons = (): Coupon[] => {
  try {
    const stored = localStorage.getItem('trax-admin-coupons');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return sampleCoupons;
};

const getLatestProducts = (): Product[] => {
  try {
    const stored = localStorage.getItem('trax-admin-products');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return defaultProducts;
};

const getLatestCategories = (): Category[] => {
  try {
    const stored = localStorage.getItem('trax-admin-categories');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return defaultCategories;
};

const getLatestOrders = (): Order[] => {
  try {
    const stored = localStorage.getItem('trax-admin-orders');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return sampleOrders;
};

const getLatestCustomers = (): Customer[] => {
  try {
    const stored = localStorage.getItem('trax-admin-customers');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return sampleCustomers;
};

const getLatestSettings = (): StoreSettings => {
  try {
    const stored = localStorage.getItem('trax-admin-settings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return defaultSettings;
};

export const useAdminStore = create<AdminState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      products: getLatestProducts(),
      orders: getLatestOrders(),
      categories: getLatestCategories(),
      customers: getLatestCustomers(),
      coupons: getLatestCoupons(),
      settings: getLatestSettings(),
      sidebarOpen: true,
      notifications: [],

      // Auth
      login: async (email: string, password: string) => {
        try {
          const response = await fetch(`${ADMIN_API_BASE()}/login.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const data = await response.json();
          if (data.success && data.token) {
            set({ isAuthenticated: true, user: data.user, token: data.token });
            return true;
          }
          return false;
        } catch (error) {
          console.error('Giriş hatası:', error);
          return false;
        }
      },

      logout: () => {
        set({ isAuthenticated: false, user: null, token: null });
      },

      fetchProductsFromAPI: async () => {
        try {
          const response = await fetch(`${PRODUCTS_API_BASE()}/list.php`);
          if (!response.ok) return;
          const data = await response.json();
          if (data.success && Array.isArray(data.products) && data.products.length > 0) {
            set({ products: data.products });
            localStorage.setItem('trax-admin-products', JSON.stringify(data.products));
          }
        } catch (error) {
          console.error('Ürünler backend\'den alınamadı:', error);
        }
      },

      fetchCategoriesFromAPI: async () => {
        try {
          const response = await fetch(`${CATEGORIES_API_BASE()}/list.php`);
          if (!response.ok) return;
          const data = await response.json();
          if (data.success && Array.isArray(data.categories) && data.categories.length > 0) {
            set({ categories: data.categories });
            localStorage.setItem('trax-admin-categories', JSON.stringify(data.categories));
          }
        } catch (error) {
          console.error('Kategoriler backend\'den alınamadı:', error);
        }
      },

      fetchCouponsFromAPI: async () => {
        try {
          const response = await fetch(`${COUPONS_API_BASE()}/list.php`, {
            headers: authHeaders(get().token),
          });
          if (!response.ok) return;
          const data = await response.json();
          if (data.success && Array.isArray(data.coupons)) {
            set({ coupons: data.coupons });
            localStorage.setItem('trax-admin-coupons', JSON.stringify(data.coupons));
          }
        } catch (error) {
          console.error('Kuponlar backend\'den alınamadı:', error);
        }
      },

      // Sidebar
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

      // Notifications
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          timestamp: Date.now(),
          read: false,
        };
        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 50),
        }));
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      markNotificationRead: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }));
      },

      markAllNotificationsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }));
      },

      // Products
      addProduct: (productData) => {
        const maxId = get().products.reduce((max, p) => Math.max(max, p.id), 0);
        const newProduct: Product = { ...productData, id: maxId + 1 };
        set((state) => {
          const updated = [...state.products, newProduct];
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/create.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify(newProduct) },
          `"${newProduct.name}" ürünü eklendi ancak sunucuya kaydedilemedi.`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'success',
              title: 'Yeni Ürün Eklendi',
              message: `"${newProduct.name}" başarıyla eklendi.`,
              icon: 'product',
            });
            get().fetchProductsFromAPI();
          }
        });
        return newProduct;
      },

      updateProduct: (id, updates) => {
        set((state) => {
          const updated = state.products.map(p => 
            p.id === id ? { ...p, ...updates } : p
          );
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, ...updates }) },
          `Ürün #${id} güncellemesi`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'info',
              title: 'Ürün Güncellendi',
              message: `Ürün #${id} başarıyla güncellendi ve sitede yayınlandı.`,
              icon: 'product',
            });
          }
        });
      },

      deleteProduct: (id) => {
        const product = get().products.find(p => p.id === id);
        set((state) => {
          const updated = state.products.filter(p => p.id !== id);
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/delete.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id }) },
          `"${product?.name || `Ürün #${id}`}" silme işlemi`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'warning',
              title: 'Ürün Silindi',
              message: `"${product?.name || `Ürün #${id}`}" silindi.`,
              icon: 'delete',
            });
          }
        });
      },

      toggleProductFeatured: (id) => {
        let newValue = false;
        set((state) => {
          const updated = state.products.map(p => {
            if (p.id === id) { newValue = !p.isFeatured; return { ...p, isFeatured: newValue }; }
            return p;
          });
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, isFeatured: newValue }) },
          `Ürün #${id} öne çıkarma durumu`
        );
      },

      toggleProductCampaign: (id) => {
        let newValue = false;
        set((state) => {
          const updated = state.products.map(p => {
            if (p.id === id) { newValue = !p.isCampaign; return { ...p, isCampaign: newValue }; }
            return p;
          });
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, isCampaign: newValue }) },
          `Ürün #${id} kampanya durumu`
        );
      },

      toggleProductNew: (id) => {
        let newValue = false;
        set((state) => {
          const updated = state.products.map(p => {
            if (p.id === id) { newValue = !p.isNew; return { ...p, isNew: newValue }; }
            return p;
          });
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, isNew: newValue }) },
          `Ürün #${id} yeni ürün durumu`
        );
      },

      updateProductStock: (id, stock) => {
        set((state) => {
          const updated = state.products.map(p => 
            p.id === id ? { ...p, stock, inStock: stock > 0 } : p
          );
          localStorage.setItem('trax-admin-products', JSON.stringify(updated));
          return { products: updated };
        });
        syncWrite(
          get().addNotification,
          `${PRODUCTS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, stock, inStock: stock > 0 }) },
          `Ürün #${id} stok güncellemesi`
        );
      },

      // Orders
      addOrder: (orderData) => {
        const newOrder: Order = {
          ...orderData,
          id: generateOrderId(),
          orderNumber: orderData.orderNumber || generateOrderNumber(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => {
          const updated = [newOrder, ...state.orders];
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        get().addNotification({
          type: 'success',
          title: 'Yeni Sipariş!',
          message: `${newOrder.customerName} tarafından ${newOrder.orderNumber} numaralı sipariş oluşturuldu.`,
          icon: 'order',
        });
        return newOrder;
      },

      updateOrderStatus: (id, status: OrderStatus) => {
        const order = get().orders.find(o => o.id === id);
        set((state) => {
          const updated = state.orders.map(o => 
            o.id === id ? { ...o, status, updatedAt: new Date().toISOString() } : o
          );
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        const statusLabels: Record<OrderStatus, string> = {
          pending: 'Beklemede',
          processing: 'Hazırlanıyor',
          shipped: 'Kargoya Verildi',
          in_transit: 'Yolda',
          delivered: 'Teslim Edildi',
          cancelled: 'İptal Edildi',
          returned: 'İade',
          return_requested: 'İade Talep Edildi',
        };
        get().addNotification({
          type: status === 'cancelled' ? 'warning' : 'info',
          title: 'Sipariş Durumu Güncellendi',
          message: `${order?.orderNumber} siparişi "${statusLabels[status]}" durumuna geçirildi.`,
          icon: 'status',
        });

        // Veritabanına yaz — orderNumber, backend'deki order_id koloniyle eşleşir.
        // Bu olmadan değişiklik sadece tarayıcıda kalır ve 30 sn'lik otomatik
        // senkronizasyonda eski haliyle üzerine yazılır.
        if (order?.orderNumber) {
          syncWrite(
            get().addNotification,
            `${ORDERS_API_BASE()}/status.php`,
            { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ orderId: order.orderNumber, status }) },
            `${order.orderNumber} sipariş durumu`
          );
        }
      },

      updateOrderPaymentStatus: (id, paymentStatus) => {
        const order = get().orders.find(o => o.id === id);
        set((state) => {
          const updated = state.orders.map(o => 
            o.id === id ? { ...o, paymentStatus, updatedAt: new Date().toISOString() } : o
          );
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        if (order?.orderNumber) {
          syncWrite(
            get().addNotification,
            `${ORDERS_API_BASE()}/status.php`,
            { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ orderId: order.orderNumber, paymentStatus }) },
            `${order.orderNumber} ödeme durumu`
          );
        }
      },

      updateOrderNotes: (id, notes) => {
        const order = get().orders.find(o => o.id === id);
        set((state) => {
          const updated = state.orders.map(o => 
            o.id === id ? { ...o, notes, updatedAt: new Date().toISOString() } : o
          );
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        if (order?.orderNumber) {
          syncWrite(
            get().addNotification,
            `${ORDERS_API_BASE()}/status.php`,
            { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ orderId: order.orderNumber, notes }) },
            `${order.orderNumber} not güncellemesi`
          );
        }
      },

      deleteOrder: (id) => {
        const order = get().orders.find(o => o.id === id);
        set((state) => {
          const updated = state.orders.filter(o => o.id !== id);
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        get().addNotification({
          type: 'warning',
          title: 'Sipariş Silindi',
          message: `${order?.orderNumber || `Sipariş #${id}`} silindi.`,
          icon: 'delete',
        });
        // DB'den de sil — aksi halde bir sonraki senkronizasyonda sipariş geri gelir.
        if (order?.orderNumber) {
          syncWrite(
            get().addNotification,
            `${ORDERS_API_BASE()}/delete.php`,
            { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ orderId: order.orderNumber }) },
            `${order.orderNumber} silme işlemi`
          );
        }
      },

      requestReturn: (orderId, reason, description) => {
        const order = get().orders.find(o => o.id === orderId);
        if (!order) return;

        set((state) => {
          const updated = state.orders.map(o =>
            o.id === orderId
              ? {
                  ...o,
                  status: 'return_requested' as OrderStatus,
                  returnRequest: {
                    reason,
                    description,
                    requestedAt: new Date().toISOString(),
                    status: 'pending' as const,
                  },
                  updatedAt: new Date().toISOString(),
                }
              : o
          );
          localStorage.setItem('trax-admin-orders', JSON.stringify(updated));
          return { orders: updated };
        });
        get().addNotification({
          type: 'warning',
          title: 'İade Talebi',
          message: `${order.orderNumber} siparişi için iade talebi oluşturuldu. Sebep: ${reason}`,
          icon: 'status',
        });

        if (order.orderNumber) {
          fetch(`${ORDERS_API_BASE()}/return.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId: order.orderNumber, reason, description }),
          }).catch((err) => console.error('İade talebi backend\'e kaydedilemedi:', err));
        }
      },

      // Fetch orders from PHP backend API (real-time sync)
      fetchOrdersFromAPI: async () => {
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          const response = await fetch(`${API_BASE_URL}/api/orders/list.php`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!response.ok) return;
          const data = await response.json();
          if (!data.success || !data.orders) return;

          // Map backend orders to admin store Order format
          const apiOrders: Order[] = data.orders.map((o: any) => ({
            id: `ORD-${o.id || o.order_id}`,
            orderNumber: o.order_number || o.order_id,
            customerName: o.customer_name,
            customerEmail: o.customer_email,
            customerPhone: o.customer_phone,
            shippingAddress: o.shipping_address || '',
            billingAddress: o.billing_address || o.shipping_address || '',
            city: o.city || '',
            district: o.district || '',
            postalCode: o.postal_code || '',
            items: o.items ? o.items.map((item: any) => ({
              product: { id: item.product_id || 0, name: item.name, regularPrice: item.unit_price, salePrice: item.unit_price, images: [] } as any,
              quantity: item.quantity,
            })) : [],
            totalAmount: parseFloat(o.total) || 0,
            subtotal: parseFloat(o.subtotal) || 0,
            shippingCost: parseFloat(o.shipping_cost) || 0,
            couponCode: o.coupon_code || '',
            couponDiscount: parseFloat(o.coupon_discount) || 0,
            status: (o.status || 'pending') as OrderStatus,
            paymentMethod: 'credit_card',
            paymentStatus: (o.payment_status === 'success' ? 'paid' : o.payment_status || 'pending') as Order['paymentStatus'],
            trackingNumber: o.tracking_number || '',
            cargoCompany: o.cargo_company || '',
            notes: o.notes || '',
            createdAt: o.created_at || new Date().toISOString(),
            updatedAt: o.updated_at || new Date().toISOString(),
          }));

          // Yeni gelen siparişleri tespit et (daha önce hiç görülmemiş orderNumber'lar)
          // ve admin'e görünür bir bildirim olarak düş — bu sayede yeni sipariş
          // sessizce listeye eklenmiyor, admin fark ediyor.
          const previouslyKnown = new Set(get().orders.map(o => o.orderNumber));
          const brandNewOrders = apiOrders.filter(o => !previouslyKnown.has(o.orderNumber));
          // İlk yüklemede (panel yeni açıldığında) mevcut tüm siparişler için
          // bildirim yağdırmamak adına, store'da hiç sipariş yoksa atla.
          if (previouslyKnown.size > 0) {
            brandNewOrders.forEach((o) => {
              get().addNotification({
                type: 'success',
                title: 'Yeni Sipariş!',
                message: `${o.orderNumber} numaralı yeni sipariş alındı — ${o.customerName} (₺${o.totalAmount.toFixed(2)})`,
                icon: 'order',
              });
            });
          }

          // Merge: API orders take priority, keep localStorage-only orders
          set((state) => {
            const existingIds = new Set(apiOrders.map(o => o.id));
            const localStorageOnly = state.orders.filter(o => !existingIds.has(o.id));
            const merged = [...apiOrders, ...localStorageOnly].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            localStorage.setItem('trax-admin-orders', JSON.stringify(merged));
            return { orders: merged };
          });

          // Sipariş veren her müşteriyi otomatik olarak Müşteriler listesine ekle/güncelle.
          // Bu olmadan admin, siteden gerçekten sipariş veren kişileri Müşteriler
          // sayfasında hiç göremez — sadece elle eklediği kayıtları görürdü.
          set((state) => {
            const byEmail = new Map(state.customers.map(c => [c.email.toLowerCase(), c]));
            const allOrders = state.orders;

            apiOrders.forEach((o) => {
              if (!o.customerEmail) return;
              const emailKey = o.customerEmail.toLowerCase();
              const custOrders = allOrders.filter(x => x.customerEmail?.toLowerCase() === emailKey);
              const totalSpent = custOrders.reduce((sum, x) => sum + (x.totalAmount || 0), 0);
              const lastOrder = custOrders.reduce((latest, x) =>
                !latest || new Date(x.createdAt) > new Date(latest.createdAt) ? x : latest, null as Order | null);
              const existing = byEmail.get(emailKey);

              byEmail.set(emailKey, {
                id: existing?.id || generateCustomerId(),
                name: o.customerName || existing?.name || '',
                email: o.customerEmail,
                phone: o.customerPhone || existing?.phone || '',
                address: o.shippingAddress || existing?.address || '',
                city: o.city || existing?.city || '',
                district: o.district || existing?.district || '',
                postalCode: o.postalCode || existing?.postalCode || '',
                notes: existing?.notes || '',
                totalOrders: custOrders.length,
                totalSpent,
                lastOrderDate: lastOrder?.createdAt || existing?.lastOrderDate || null,
                createdAt: existing?.createdAt || o.createdAt,
                updatedAt: new Date().toISOString(),
              });
            });

            const updatedCustomers = Array.from(byEmail.values());
            localStorage.setItem('trax-admin-customers', JSON.stringify(updatedCustomers));
            return { customers: updatedCustomers };
          });
        } catch (error) {
          console.error('Failed to fetch orders from API:', error);
        }
      },

      // Customers
      addCustomer: (customerData) => {
        const newCustomer: Customer = {
          ...customerData,
          id: generateCustomerId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => {
          const updated = [...state.customers, newCustomer];
          localStorage.setItem('trax-admin-customers', JSON.stringify(updated));
          return { customers: updated };
        });
        get().addNotification({
          type: 'success',
          title: 'Yeni Müşteri',
          message: `"${newCustomer.name}" müşteri listesine eklendi.`,
          icon: 'order',
        });
        return newCustomer;
      },

      updateCustomer: (id, updates) => {
        set((state) => {
          const updated = state.customers.map(c =>
            c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
          );
          localStorage.setItem('trax-admin-customers', JSON.stringify(updated));
          return { customers: updated };
        });
        get().addNotification({
          type: 'info',
          title: 'Müşteri Güncellendi',
          message: `Müşteri bilgileri güncellendi.`,
          icon: 'order',
        });
      },

      deleteCustomer: (id) => {
        const customer = get().customers.find(c => c.id === id);
        set((state) => {
          const updated = state.customers.filter(c => c.id !== id);
          localStorage.setItem('trax-admin-customers', JSON.stringify(updated));
          return { customers: updated };
        });
        get().addNotification({
          type: 'warning',
          title: 'Müşteri Silindi',
          message: `"${customer?.name || `Müşteri #${id}`}" silindi.`,
          icon: 'delete',
        });
      },

      getCustomerById: (id) => get().customers.find(c => c.id === id),
      getCustomerByEmail: (email) => get().customers.find(c => c.email === email),
      getCustomerOrders: (customerId) => {
        const customer = get().customers.find(c => c.id === customerId);
        if (!customer) return [];
        return get().orders.filter(o => 
          o.customerEmail === customer.email || o.customerName === customer.name
        );
      },

      // Categories
      addCategory: (category) => {
        set((state) => {
          const updated = [...state.categories, category];
          localStorage.setItem('trax-admin-categories', JSON.stringify(updated));
          return { categories: updated };
        });
        syncWrite(
          get().addNotification,
          `${CATEGORIES_API_BASE()}/create.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify(category) },
          `"${category.name}" kategorisi`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'success',
              title: 'Yeni Kategori',
              message: `"${category.name}" kategorisi eklendi ve sitede yayınlandı.`,
              icon: 'product',
            });
          }
        });
      },

      updateCategory: (id, updates) => {
        set((state) => {
          const updated = state.categories.map(c => 
            c.id === id ? { ...c, ...updates } : c
          );
          localStorage.setItem('trax-admin-categories', JSON.stringify(updated));
          return { categories: updated };
        });
        syncWrite(
          get().addNotification,
          `${CATEGORIES_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, ...updates }) },
          `"${id}" kategorisi güncellemesi`
        );
      },

      deleteCategory: (id) => {
        const cat = get().categories.find(c => c.id === id);
        set((state) => {
          const updated = state.categories.filter(c => c.id !== id);
          localStorage.setItem('trax-admin-categories', JSON.stringify(updated));
          return { categories: updated };
        });
        syncWrite(
          get().addNotification,
          `${CATEGORIES_API_BASE()}/delete.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id }) },
          `"${cat?.name || id}" kategorisi silme işlemi`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'warning',
              title: 'Kategori Silindi',
              message: `"${cat?.name || id}" kategorisi silindi.`,
              icon: 'delete',
            });
          }
        });
      },

      // Coupons
      addCoupon: (couponData) => {
        const newCoupon: Coupon = {
          ...couponData,
          id: generateCouponId(),
          usageCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => {
          const updated = [...state.coupons, newCoupon];
          localStorage.setItem('trax-admin-coupons', JSON.stringify(updated));
          return { coupons: updated };
        });
        syncWrite(
          get().addNotification,
          `${COUPONS_API_BASE()}/create.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify(newCoupon) },
          `"${newCoupon.code}" kuponu`
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'success',
              title: 'Yeni Kupon',
              message: `"${newCoupon.code}" kupon kodu oluşturuldu ve kullanıma açıldı.`,
              icon: 'product',
            });
          }
        });
        return newCoupon;
      },

      updateCoupon: (id, updates) => {
        set((state) => {
          const updated = state.coupons.map(c =>
            c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
          );
          localStorage.setItem('trax-admin-coupons', JSON.stringify(updated));
          return { coupons: updated };
        });
        syncWrite(
          get().addNotification,
          `${COUPONS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, ...updates }) },
          `Kupon #${id} güncellemesi`
        );
      },

      deleteCoupon: (id) => {
        const coupon = get().coupons.find(c => c.id === id);
        set((state) => {
          const updated = state.coupons.filter(c => c.id !== id);
          localStorage.setItem('trax-admin-coupons', JSON.stringify(updated));
          return { coupons: updated };
        });
        syncWrite(
          get().addNotification,
          `${COUPONS_API_BASE()}/delete.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id }) },
          `"${coupon?.code || id}" kuponu silme işlemi`
        );
        get().addNotification({
          type: 'warning',
          title: 'Kupon Silindi',
          message: `"${coupon?.code || id}" kuponu silindi.`,
          icon: 'delete',
        });
      },

      toggleCouponActive: (id) => {
        let newValue = false;
        set((state) => {
          const updated = state.coupons.map(c => {
            if (c.id === id) { newValue = !c.isActive; return { ...c, isActive: newValue, updatedAt: new Date().toISOString() }; }
            return c;
          });
          localStorage.setItem('trax-admin-coupons', JSON.stringify(updated));
          return { coupons: updated };
        });
        syncWrite(
          get().addNotification,
          `${COUPONS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify({ id, isActive: newValue }) },
          `Kupon #${id} durum değişikliği`
        );
      },

      validateCoupon: async (code, orderAmount) => {
        try {
          const response = await fetch(`${COUPONS_API_BASE()}/validate.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, orderAmount }),
          });
          const data = await response.json();
          if (data.valid) {
            return { valid: true, discount: data.discount, coupon: data.coupon };
          }
          return { valid: false, discount: 0, error: data.error || 'Geçersiz kupon' };
        } catch (error) {
          console.error('Kupon doğrulanamadı:', error);
          return { valid: false, discount: 0, error: 'Kupon doğrulanamadı, internet bağlantınızı kontrol edin.' };
        }
      },

      // Settings
      updateSettings: (newSettings) => {
        set((state) => {
          const updated = { ...state.settings, ...newSettings };
          localStorage.setItem('trax-admin-settings', JSON.stringify(updated));
          return { settings: updated };
        });
        syncWrite(
          get().addNotification,
          `${SETTINGS_API_BASE()}/update.php`,
          { method: 'POST', headers: authHeaders(get().token), body: JSON.stringify(newSettings) },
          'Mağaza ayarları'
        ).then((ok) => {
          if (ok) {
            get().addNotification({
              type: 'success',
              title: 'Ayarlar Güncellendi',
              message: 'Mağaza ayarları kaydedildi ve sitede yayınlandı.',
              icon: 'system',
            });
          }
        });
      },

      fetchSettingsFromAPI: async () => {
        try {
          const response = await fetch(`${SETTINGS_API_BASE()}/get.php`);
          if (!response.ok) return;
          const data = await response.json();
          if (data.success && data.settings) {
            set({ settings: data.settings });
            localStorage.setItem('trax-admin-settings', JSON.stringify(data.settings));
          }
        } catch (error) {
          console.error('Ayarlar backend\'den alınamadı:', error);
        }
      },

      // Helpers
      getProductById: (id) => get().products.find(p => p.id === id),
      getOrderById: (id) => get().orders.find(o => o.id === id),
      getOrdersByStatus: (status) => get().orders.filter(o => o.status === status),
      
      getTotalRevenue: () => 
        get().orders
          .filter(o => o.paymentStatus === 'paid')
          .reduce((sum, o) => sum + o.totalAmount, 0),

      getTotalOrders: () => get().orders.length,
      getTotalProducts: () => get().products.length,
      
      getRecentOrders: (count) => 
        [...get().orders]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, count),

      getTotalCustomers: () => get().customers.length,
    }),
    {
      name: 'trax-admin',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
);
